import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    OnChanges,
    SimpleChanges,
    OnDestroy
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';
import {
    getDefaultValue,
    JourneyPlannerFilter
} from '@ridango/components/lib/open-trip-planner/journey-planner-filter.model';
import { OpenTripPlannerService } from '@ridango/components';

@Component({
    selector: 'rwidget-journey-planner-filter',
    templateUrl: './journey-planner-filter.component.html'
})
export class JourneyPlannerFilterComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input() currentFilter: JourneyPlannerFilter = { routes: [] };
    @Output() filterChanged: EventEmitter<JourneyPlannerFilter> = new EventEmitter<JourneyPlannerFilter>();
    @Output() filterReady: EventEmitter<void> = new EventEmitter<void>();

    showRoutes = this.env.JOURNEY_PLANNER?.FILTERS?.ROUTE ?? true;
    formGroup: FormGroup;
    transportModes = [];
    transportModeValues = [];
    readonly maxWalkDistanceSelector = this.env.JOURNEY_PLANNER?.FILTERS?.SELECTORS.find(
        (selector) => selector.TITLE_KEY === 'JOURNEY_PLANNER_FILTER_MAX_WALK_DISTANCE_TITLE'
    );

    constructor(
        @Inject('ENV') public env: Env,
        private otpService: OpenTripPlannerService,
        private formBuilder: FormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.env.JOURNEY_PLANNER?.FILTERS?.TRANSPORT_MODE) {
            this.otpService
                .getMeansOfTransport()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (meansOfTransport) => {
                        this.transportModes = meansOfTransport.transportModes;
                        this.transportModeValues = meansOfTransport.transportModes.map((mode) => mode.translationTag);

                        if (!this.currentFilter?.transportModes) {
                            const transportModesFormValue = meansOfTransport.transportModes
                                .filter((mode) => mode.defaultChecked)
                                .map((mode) => mode.translationTag);

                            this.currentFilter = {
                                ...this.currentFilter,
                                transportModes: transportModesFormValue
                            };
                        }
                        if (!this.formGroup) {
                            this.setInitialState();
                        } else {
                            this.formGroup.patchValue({
                                transportModes: this.currentFilter.transportModes
                            });
                        }
                    },
                    () => {
                        this.setInitialState();
                    }
                );
        } else {
            this.setInitialState();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes['currentFilter'].currentValue || !changes['currentFilter'].previousValue || !this.formGroup) {
            return;
        }

        if (
            this.env.JOURNEY_PLANNER?.FILTERS?.TRANSPORT_MODE &&
            !changes['currentFilter'].currentValue?.transportModes &&
            this.transportModes?.length
        ) {
            this.formGroup.patchValue({
                transportModes: this.transportModes
                    .filter((mode) => mode.defaultChecked)
                    .map((mode) => mode.translationTag)
            });
        } else if (
            JSON.stringify(changes['currentFilter'].currentValue?.transportModes) !==
            JSON.stringify(changes['currentFilter'].previousValue?.transportModes)
        ) {
            this.formGroup.patchValue({ transportModes: changes['currentFilter'].currentValue?.transportModes });
        }

        if (
            this.env.JOURNEY_PLANNER?.FILTERS?.ROUTE &&
            JSON.stringify(changes['currentFilter'].currentValue?.routes) !==
                JSON.stringify(changes['currentFilter'].previousValue?.routes)
        ) {
            this.formGroup.patchValue({ routes: changes['currentFilter'].currentValue?.routes });
        }

        this.env.JOURNEY_PLANNER?.FILTERS?.SELECTORS?.forEach((selector) => {
            const queryParam = selector.QUERY_PARAMETER;
            const currentValue = changes['currentFilter'].currentValue?.[queryParam];
            const previousValue = changes['currentFilter'].previousValue?.[queryParam];

            switch (selector.TYPE) {
                case 'MULTI_SELECTION':
                    if (JSON.stringify(currentValue) === JSON.stringify(previousValue)) {
                        return;
                    }
                    this.formGroup.patchValue({ [queryParam]: currentValue });
                    break;
                case 'SINGLE_SELECTION':
                    if (currentValue === previousValue) {
                        return;
                    }
                    this.formGroup.patchValue({ [queryParam]: currentValue });
                    break;
                case 'SLIDER':
                    if (currentValue === previousValue) {
                        return;
                    }
                    if (queryParam === 'maxWalkDistance') {
                        const currentValueIndex = this.maxWalkDistanceSelector.VALUES.findIndex(
                            (el) => el.VALUE === currentValue
                        );
                        this.formGroup.patchValue({
                            [queryParam]: currentValueIndex === -1 ? 0 : currentValueIndex
                        });
                    } else {
                        this.formGroup.patchValue({ [queryParam]: currentValue });
                    }
                    break;
                case 'VIA_POINT':
                    if (currentValue === previousValue) {
                        return;
                    }
                    this.formGroup.patchValue({ [queryParam]: currentValue });
                    break;
                default:
                    break;
            }
        });
    }

    private setInitialState(): void {
        const defaultValue: any = getDefaultValue(this.env.JOURNEY_PLANNER?.FILTERS);
        const currentValue: any = { ...this.currentFilter };
        const initialValueMaxWalkDistance = -1;

        Object.keys(defaultValue).forEach((key) => (defaultValue[key] = [defaultValue[key]]));
        Object.keys(currentValue).forEach((key) => (currentValue[key] = [currentValue[key]]));

        if (currentValue.maxWalkDistance) {
            const currentValueIndex = this.maxWalkDistanceSelector?.VALUES.findIndex(
                (el) => el.VALUE === currentValue.maxWalkDistance[0]
            );

            if (currentValueIndex >= 0 || currentValueIndex !== undefined) {
                currentValue.maxWalkDistance = [currentValueIndex];
            }

            if (currentValue.maxWalkDistance[0] === initialValueMaxWalkDistance) {
                currentValue.maxWalkDistance = [this.maxWalkDistanceSelector.INIT_VALUE];
            }
        }

        this.formGroup = this.formBuilder.group({
            ...defaultValue,
            ...currentValue
        });
        this.filterReady.emit();

        this.formGroup.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe((changes) => {
            this.filterChanged.emit({
                ...changes,
                transportModesValue: this.transportModes
                    .filter((mode) => changes.transportModes.includes(mode.translationTag))
                    .map((mode) => mode.id)
                    .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
            });
        });
    }

    filterRouteChangedHandler(filter: string[]) {
        this.formGroup.patchValue({ routes: filter });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
